import React, { useState, useEffect } from "react";
import { getAuthenticateUrl, getFeedbackSubmitUrl } from "../utils/QueryParam";
import { StarIcon, logo, lsqLogog, fourofour, LikeIcon, DislikeIcon, DissatisfiedIcon, SatisfiedIcon } from "../utils/assets";

const API_HOST = process.env.REACT_APP_SHARED_SERVICE_API_HOST;

const SatisfactionSurvey = () => {
  const [rating, setRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentScreen, setCurrentScreen] = useState("loading");
  const [question, setQuestion] = useState(null);
  const [csatSettings, setCsatSettings] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");
  const [token, setToken] = useState(null);
  const [type, setType] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("identity");
    const fetchedType = urlParams.get("type");
    if (fetchedType) setType(fetchedType);
    setToken(code);

    if (code) {
      authenticateUser(code, fetchedType);
    } else {
      setCurrentScreen("404");
    }
  }, []);

  const authenticateUser = async (code, fetchedType) => {
    try {
      const response = await fetch(
        `${getAuthenticateUrl(API_HOST, code, fetchedType)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data) {
          if (data.csatDetails.length > 0) setQuestion(data.csatDetails[0]);
          if (data.csatSettings.length > 0) setCsatSettings(data.csatSettings);
          setCurrentScreen("rating");
        } else {
          setCurrentScreen("404");
        }
      } else {
        if (response.status === 401) {
          console.log("401");
          setCurrentScreen("401");
        } else {
          setCurrentScreen("404");
        }
      }
    } catch (error) {
      console.error("Error authenticating user:", error);
      setCurrentScreen("404");
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setSubmitMessage("Please select a rating before submitting.");
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const surveyData = {
        csatDetails: [
          {
            ...question,
            rating: rating,
          },
        ],
        csatComments: feedbackComment,
      };

      const response = await fetch(
        `${getFeedbackSubmitUrl(API_HOST, token, type)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(surveyData),
        }
      );

      if (response.ok) {
        setCurrentScreen("feedback");
      } else {
        setSubmitMessage("An error occurred. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting survey:", error);
      setSubmitMessage("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCtaLabel = () => {
    const ctaLabelSettings = csatSettings.filter(
      (csatSetting) => csatSetting.settingName.toLowerCase() === "ctalabel"
    );
    if (ctaLabelSettings && ctaLabelSettings.length)
      return ctaLabelSettings[0].settingConfig.ctaLabelText;
    return "Submit Survey";
  };

  const getCommentSettings = () => {
    const commentSettings = csatSettings.filter(
      (csatSetting) => csatSetting.settingName.toLowerCase() === "comment"
    );
    if (commentSettings && commentSettings.length) {
      return {
        enabled: commentSettings[0].settingConfig.commentEnabled,
        commentText: commentSettings[0].settingConfig.commentText
          ? commentSettings[0].settingConfig.commentText
          : "How can we improve your experience?",
      };
    }
    return {
      enabled: false,
      commentText: "",
    };
  };

  const onFeedbackChange = (event) => {
    setFeedbackComment(event?.target?.value || "");
  };

  const renderRatingScreen = () => {
    const commentSettings = getCommentSettings();

    return (
      <>
        {question && <h2 className="survey-title">{question.questionText}</h2>}
        {question && question.ratingTypeId === 1 && (
          <>
            <div className="star-container">
              {[1, 2, 3, 4, 5].map((star) => (
                <img
                  key={star}
                  src={StarIcon}
                  onClick={() => setRating(star)}
                  className={`star ${star <= rating ? "active" : ""}`}
                  alt="star"
                />
              ))}
            </div>
            <div className="rating-labels">
              <span>Extremely Dissatisfied</span>
              <span>Extremely Satisfied</span>
            </div>
          </>
        )}
        {question && question.ratingTypeId === 2 && (
          <div className="thumbs-container">
            <div className="thumb-container">
              <img src={ rating === 1 ? DissatisfiedIcon : DislikeIcon } className="thumb-icon" onClick={() => setRating(1)} alt="Dislike" />
              <span className="thumb-icon-text">Dissatisfied</span>
            </div>
            <div className="thumb-container">
              <img src={ rating === 5 ? SatisfiedIcon : LikeIcon } className="thumb-icon" onClick={() => setRating(5)} alt="Like" />
              <span className="thumb-icon-text">Satisfied</span>
            </div>
          </div>
        )}
        {commentSettings.enabled && (
          <div className="comments-container">
            <textarea
              className="comments-input"
              placeholder={commentSettings.commentText}
              onChange={(e) => onFeedbackChange(e)}
              maxLength={200}
            />
            <span className="comments-input-counter">
              {feedbackComment.length}/200
            </span>
          </div>
        )}
        <button
          className="submit-button"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : getCtaLabel()}
        </button>
      </>
    );
  };

  const renderErrorScreen = () => (
    <div className="error-404">
      <div className="feedback-submission-content">
        <img src={fourofour} alt="404 icon" className="erroricon" />

        {currentScreen === "401" && (
          <p className="feedback-submission-subheader">
            We’re sorry, the survey link has expired.
          </p>
        )}
        {currentScreen === "404" && (
          <div>
            <h1 className="feedback-submission-header">Page Not Found</h1>
            <p className="feedback-submission-subheader">
              We're sorry, the page you requested <br></br>could not be found.
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderLoadingScreen = () => (
    <div className="loading-screen">
      <div className="loader"></div>
      {/* <p>Loading...</p> */}
    </div>
  );

  const FeedbackSubmission = () => (
    <div className="feedback-submission-content">
      <img src={logo} alt="Chat icon" className="icon" />

      <p className="feedback-submission-header">
        Your response has been submitted!
      </p>
      <p className="feedback-submission-subheader">{question.thankYouMsg}</p>
    </div>
  );

  if (currentScreen === "loading") {
    return <div className="gradient-background"> {renderLoadingScreen()}</div>;
  }

  return (
    <div className="gradient-background">
      <div className="content-wrapper">
        <div className="survey-container">
          {["404", "401"].includes(currentScreen) && renderErrorScreen()}
          {currentScreen === "rating" && renderRatingScreen()}
          {currentScreen === "feedback" && FeedbackSubmission()}
          {submitMessage && (
            <div className="submit-message">{submitMessage}</div>
          )}
        </div>
      </div>
      <div className="powered-by">
        <p>powered by</p>
        <img src={lsqLogog} alt="LeadSquared" className="leadsquared-logo" />
      </div>
    </div>
  );
};

export default SatisfactionSurvey;
